import { HTMLAttributes } from 'react'

import Media from '@/components/base/media'
import Typography from '@/components/base/typography'
import { sanitizeAdsLink } from '@/utils/path'

import { cn } from '@/lib/utils'
import { WPImage } from '@/types'

type ImageTextProps = HTMLAttributes<HTMLDivElement> &
  Partial<{
    image: WPImage
    title: string
    paragraph: string
    link: {
      url: string
      title: string
      target: string
    }
    align: 'left' | 'right'
    orientation: 'portrait' | 'landscape'
  }>

const ImageText = ({
  image,
  title,
  paragraph,
  link,
  className,
  align,
  orientation
}: ImageTextProps) => {
  return (
    <div className={cn('px-[3rem] md:px-[10rem]', className)}>
      <div
        className={cn(
          'grid grid-cols-1 gap-[4rem] md:grid-cols-2',
          align === 'right'
            ? 'md:[&>*:first-child]:order-2 md:[&>*:nth-child(2)]:order-1'
            : ''
        )}
      >
        {/* Image - always visible on mobile, regardless of alignment */}
        <div className="flex justify-center">
          {image ? (
            <div
              className={cn(
                'relative z-10 flex w-full justify-center overflow-hidden rounded-lg',
                { 'aspect-[3/4] md:w-3/4': orientation === 'portrait' },
                { 'aspect-[4/3]': orientation === 'landscape' }
              )}
            >
              <Media
                fill
                fit="cover"
                use_video={false}
                image={image}
                data-motion-media
              />
            </div>
          ) : null}
        </div>

        {/* Text content */}
        <div className="relative flex flex-col justify-center gap-[2rem]">
          <div className="flex flex-col justify-center gap-[2rem]">
            {title ? (
              <Typography
                component="h1"
                className="text-[3.6rem] font-light leading-[1]"
              >
                {title}
              </Typography>
            ) : null}
            {paragraph ? (
              <Typography component="p" className="text-[1.8rem]">
                {paragraph}
              </Typography>
            ) : null}
            {link?.url ? (
              <a
                href={sanitizeAdsLink(link.url)}
                target={link.target}
                className={cn('button', className)}
              >
                <Typography component="p">{link.title}</Typography>
              </a>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ImageText
